import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChartPositionContext } from '../contexts/ChartPositionContext';
import { useTheme } from '../contexts/ThemeContext';
import { DragDropProvider } from '../contexts/DragDropContext';
import DraggablePositionOverlay from './DraggablePositionOverlay';
import DragGhost from './DragGhost';

interface ChartPositionManagerProps {
  chartRef: React.RefObject<any>;
  seriesRef: any;
  symbol: string;
  currentPrice: number;
  isVisible: boolean;
}

const ChartPositionManager: React.FC<ChartPositionManagerProps> = ({
  chartRef,
  seriesRef,
  symbol,
  currentPrice,
  isVisible
}) => {
  const { chartPositions, syncWithPositions, syncWithPendingOrders } = useChartPositionContext();
  const overlayRef = useRef<HTMLDivElement>(null);
  const priceLinesRef = useRef<any[]>([]);
  const isMountedRef = useRef(true);

  // Sync positions and pending orders when symbol or data changes
  useEffect(() => {
    syncWithPositions(symbol);
    syncWithPendingOrders(symbol);
  }, [symbol, syncWithPositions, syncWithPendingOrders]);


  // Filter positions for current symbol and visible ones
  const symbolPositions = chartPositions.filter(pos => pos.symbol === symbol && pos.isVisible !== false);


  // Add position lines to the chart using lightweight-charts API
  useEffect(() => {
    if (!seriesRef || !isVisible || symbolPositions.length === 0) return;

    // Check if series is still valid and not disposed
    if (!seriesRef.createPriceLine || typeof seriesRef.createPriceLine !== 'function') {
      console.warn('Series is not valid or disposed');
      return;
    }

    // Check if chart is disposed by trying to access a property
    try {
      // Try to access a simple property to check if series is still valid
      if (seriesRef._internal_series && seriesRef._internal_series._chart) {
        const chart = seriesRef._internal_series._chart;
        if (chart._internal_chart && chart._internal_chart._disposed) {
          console.warn('Chart is disposed, skipping position lines');
          return;
        }
      }
    } catch (error) {
      // If we can't access the chart, it might be disposed
      console.warn('Cannot access chart, might be disposed');
      return;
    }

    // Add a small delay to prevent rapid updates
    const timeoutId = setTimeout(() => {
      // Check if component is still mounted
      if (!isMountedRef.current) {
        return;
      }

      // Double-check series is still valid after delay
      if (!seriesRef || !seriesRef.createPriceLine || typeof seriesRef.createPriceLine !== 'function') {
        return;
      }

      // Check again if chart is disposed
      try {
        if (seriesRef._internal_series && seriesRef._internal_series._chart) {
          const chart = seriesRef._internal_series._chart;
          if (chart._internal_chart && chart._internal_chart._disposed) {
            console.warn('Chart is disposed during timeout, skipping position lines');
            return;
          }
        }
      } catch (error) {
        console.warn('Cannot access chart during timeout, might be disposed');
        return;
      }

    // Clear existing price lines
    priceLinesRef.current.forEach(line => {
      try {
        if (seriesRef && seriesRef.removePriceLine && typeof seriesRef.removePriceLine === 'function') {
          seriesRef.removePriceLine(line);
        }
      } catch (error) {
        // Ignore errors when removing lines from disposed series
        if (error instanceof Error && !error.message?.includes('disposed')) {
          console.warn('Error removing price line:', error);
        }
      }
    });
    priceLinesRef.current = [];

    // Add position lines for each position
    // console.log(`Rendering ${symbolPositions.length} chart positions for ${symbol}`);
    symbolPositions.forEach((position, index) => {
      try {
        // Check if component is still mounted before each operation
        if (!isMountedRef.current) {
          return;
        }

        const entryPrice = parseFloat(position.entry);
        const isBuy = position.type === 'Buy';
        
        if (position.isPendingOrder) {
          // console.log(`Rendering pending order ${position.id} at price ${position.entry}`);
        }
        
        // Add horizontal line at entry price
        const priceLine = {
          price: entryPrice,
          color: isBuy ? '#2AB7A6' : '#ef4444',
          lineWidth: position.isAI ? 2 : 1, // Thicker line for AI positions
          lineStyle: 0, // Solid line
          axisLabelVisible: true,
          title: `${position.type} ${parseFloat(position.size).toFixed(2)} @ $${parseFloat(position.entry).toFixed(2)}${position.isAI ? ' (AI)' : ''}`,
        };

        // Add to the main series
        const entryLine = seriesRef.createPriceLine(priceLine);
        priceLinesRef.current.push(entryLine);

        // Add SL line if set (initially at same price as entry)
        const slPrice = position.slLevel || entryPrice;
        const slLine = {
          price: slPrice,
          color: '#ef4444',
          lineWidth: 1,
          lineStyle: 3, // Dotted line
          axisLabelVisible: true,
          title: `SL: $${slPrice.toFixed(2)}`,
        };
        const slPriceLine = seriesRef.createPriceLine(slLine);
        priceLinesRef.current.push(slPriceLine);

        // Add TP line if set (initially at same price as entry)
        const tpPrice = position.tpLevel || entryPrice;
        const tpLine = {
          price: tpPrice,
          color: '#10b981',
          lineWidth: 1,
          lineStyle: 3, // Dotted line
          axisLabelVisible: true,
          title: `TP: $${tpPrice.toFixed(2)}`,
        };
        const tpPriceLine = seriesRef.createPriceLine(tpLine);
        priceLinesRef.current.push(tpPriceLine);
      } catch (error) {
        // Ignore errors when series is disposed
        if (error instanceof Error && !error.message?.includes('disposed')) {
          console.warn('Error creating price line:', error);
        }
      }
    });

    }, 100); // 100ms delay

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      priceLinesRef.current.forEach(line => {
        try {
          if (seriesRef && seriesRef.removePriceLine && typeof seriesRef.removePriceLine === 'function') {
            seriesRef.removePriceLine(line);
          }
        } catch (error) {
          // Ignore errors when removing lines from disposed series
          if (error instanceof Error && !error.message?.includes('disposed')) {
            console.warn('Error removing price line:', error);
          }
        }
      });
      priceLinesRef.current = [];
    };
  }, [seriesRef, isVisible, symbolPositions]);




  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
      
      // Clear all price lines when component unmounts
      priceLinesRef.current.forEach(line => {
        try {
          if (seriesRef && seriesRef.removePriceLine && typeof seriesRef.removePriceLine === 'function') {
            seriesRef.removePriceLine(line);
          }
        } catch (error) {
          // Ignore disposal errors
          if (error instanceof Error && !error.message?.includes('disposed')) {
            console.warn('Error removing price line on unmount:', error);
          }
        }
      });
      priceLinesRef.current = [];
    };
  }, [seriesRef]);

  if (!isVisible || symbolPositions.length === 0) {
    return null;
  }

  return (
    <DragDropProvider>
    <div
      ref={overlayRef}
      className="absolute inset-0 pointer-events-none z-20 chartpositions"
      style={{ 
        zIndex: 20
      }}
    >
        {/* Position Labels - TradingView style with drag and drop */}
        {symbolPositions.map((position, index) => (
          <DraggablePositionOverlay
            key={`position-${position.id}-${index}`}
            position={position}
            index={index}
            currentPrice={currentPrice}
            chartRef={chartRef}
            overlayRef={overlayRef}
          />
        ))}
                </div>
      <DragGhost />
    </DragDropProvider>
  );
};

export default ChartPositionManager;
